import { motion } from "framer-motion";
import { Search, FileText, Sparkles, Star } from "lucide-react";
import { useState, useEffect } from "react";

export const SearchAIMockup = () => {
  const [showResults, setShowResults] = useState(false);
  const [typedText, setTypedText] = useState("");
  const fullText = "facture EDF";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTypedText("");
          setShowResults(false);
          
          let i = 0;
          const typeInterval = setInterval(() => {
            if (i < fullText.length) {
              setTypedText(fullText.slice(0, i + 1));
              i++;
            } else {
              clearInterval(typeInterval);
              setTimeout(() => setShowResults(true), 400);
            }
          }, 70);

          return () => clearInterval(typeInterval);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById("search-mockup");
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      id="search-mockup"
      className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-violet-500 p-3 md:p-4 flex flex-col gap-2 shadow-xl overflow-hidden relative"
    >
      {/* Ambient effects */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
      <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-violet-400/30 rounded-full blur-2xl" />
      
      {/* AI sparkle indicator */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        className="absolute top-2 right-2"
      >
        <Sparkles className="w-3 h-3 text-amber-300/60" />
      </motion.div>

      {/* Search bar with glassmorphism */}
      <div className="relative z-10">
        <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-xl px-2.5 py-2 shadow-lg border border-white/50">
          <Search className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className="text-[10px] md:text-xs text-foreground font-medium truncate flex-1">
            {typedText}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="inline-block w-0.5 h-3 bg-primary ml-0.5 rounded-full"
            />
          </span>
        </div>
      </div>

      {/* Results container */}
      <div className="flex-1 relative z-10 flex flex-col gap-1.5">
        {/* Main result */}
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.95 }}
          animate={showResults ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
          className="bg-white rounded-xl p-2 shadow-lg border border-gray-100"
        >
          <div className="flex items-start gap-2">
            <div className="w-6 h-7 md:w-7 md:h-8 bg-gradient-to-br from-red-100 to-red-50 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <FileText className="w-3 h-3 md:w-3.5 md:h-3.5 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-[9px] md:text-[10px] font-semibold text-foreground truncate">
                  Facture <span className="bg-amber-200/60 px-0.5 rounded">EDF</span>
                </p>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={showResults ? { scale: 1 } : {}}
                  transition={{ delay: 0.3, type: "spring" }}
                >
                  <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500" />
                </motion.div>
              </div>
              <p className="text-[7px] md:text-[8px] text-muted-foreground">
                Sept. 2024 · 127,50€
              </p>
            </div>
          </div>
        </motion.div>

        {/* Secondary result hint */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={showResults ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.15, type: "spring", stiffness: 400 }}
          className="bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1.5 flex items-center gap-1.5"
        >
          <div className="w-4 h-5 bg-gray-100 rounded flex items-center justify-center">
            <FileText className="w-2 h-2 text-gray-400" />
          </div>
          <p className="text-[7px] text-muted-foreground truncate">
            Facture EDF Août 2024
          </p>
        </motion.div>
      </div>
    </div>
  );
};
